import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { money, formatDateAT } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export default function AdminTenantDetail() {
  const { restaurantId } = useParams();
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const { startSupport } = useAuth();

  const load = async () => {
    const { data } = await api.get(`/admin/tenants/${restaurantId}`);
    setData(data);
  };
  useEffect(() => { load(); }, [restaurantId]);

  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const r = data.restaurant;

  const addNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      await api.post(`/admin/tenants/${restaurantId}/notes`, { note });
      setNote("");
      await load();
      toast.success("Note added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  const doSupport = async () => {
    try { await startSupport(restaurantId); window.location.href = "/"; }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-tenant-detail">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold font-display tracking-tight truncate">
            {r.brand_name || r.name}
            {r.status === "suspended" && <Badge className="ml-2 bg-destructive hover:bg-destructive">Suspended</Badge>}
          </h1>
          <p className="text-sm text-muted-foreground font-mono">{r.slug} · {formatDateAT(r.created_at)}</p>
        </div>
        <Button onClick={doSupport} data-testid="detail-enter-support">Enter Support Mode</Button>
      </div>

      {data.current_month && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Current month ({data.current_month.period_key})</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><div className="text-[10px] uppercase text-muted-foreground">Revenue</div><div className="font-mono font-semibold">{money(data.current_month.total_revenue)}</div></div>
            <div><div className="text-[10px] uppercase text-muted-foreground">Expenses</div><div className="font-mono font-semibold">{money(data.current_month.total_expenses)}</div></div>
            <div><div className="text-[10px] uppercase text-muted-foreground">Profit</div><div className="font-mono font-semibold">{money(data.current_month.total_profit)}</div></div>
            <div><div className="text-[10px] uppercase text-muted-foreground">Net VAT</div><div className="font-mono font-semibold">{money(data.current_month.vat_summary?.net_vat || 0)}</div></div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Latest 20 movements</CardTitle></CardHeader>
          <CardContent>
            <ul className="divide-y">
              {(data.movements || []).map((m) => (
                <li key={`${m._kind}-${m.id}`} className="py-2 flex items-center gap-3 text-sm">
                  <Badge variant="outline" className="text-[10px]">{m._kind}</Badge>
                  <div className="flex-1 min-w-0 truncate">{m.vendor || m.party_name || m.description || m.notes || "—"}</div>
                  <span className="font-mono text-xs text-muted-foreground">{formatDateAT(m.date || m.issue_date)}</span>
                  <span className="font-mono font-semibold">{money(m.amount || m.amount_total || m.total || 0)}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Latest AI captures</CardTitle></CardHeader>
          <CardContent>
            <ul className="divide-y">
              {(data.capture_events || []).map((c) => (
                <li key={c.id} className="py-2 flex items-center gap-3 text-sm">
                  <Badge variant="outline" className="text-[10px]">{c.source}</Badge>
                  <div className="flex-1 truncate">{c.vendor || "—"} · <span className="text-xs text-muted-foreground">{c.kind}</span></div>
                  <span className="font-mono text-xs">{c.confidence != null ? Math.round(c.confidence * 100) + "%" : ""}</span>
                  <span className="font-mono">{money(c.amount || 0)}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Admin notes</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start gap-2">
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal note…" data-testid="admin-note-input" />
            <Button onClick={addNote} disabled={saving || !note.trim()} data-testid="admin-note-save">Save</Button>
          </div>
          <ul className="space-y-2">
            {(data.admin_notes || []).slice().reverse().map((n) => (
              <li key={n.id} className="border rounded-lg p-2 text-sm">
                <div className="text-xs text-muted-foreground font-mono">{n.author_email} · {formatDateAT(n.created_at)}</div>
                <div>{n.text}</div>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
